import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
} from 'react-router-dom';
import { config } from './config';
import { hasAnalyticsConsent, subscribeConsent } from './utils/cookieConsent';

// Sentry session replay records the page (including PII), so it is treated as
// an *analytics* cookie and only runs with consent. Error + performance
// monitoring itself stays on as strictly-necessary (security/stability), so we
// gate only the replay integration here.
const makeReplay = () =>
    Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: true,
        blockAllMedia: false,
    });

if (config.environment === 'production' && config.sentryDsn) {
    Sentry.init({
        dsn: config.sentryDsn,
        tunnel: '/sentry-tunnel',
        environment: config.environment,
        release: config.release,
        sendDefaultPii: true,

        integrations: [
            Sentry.reactRouterV7BrowserTracingIntegration({
                useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
            ...(hasAnalyticsConsent() ? [makeReplay()] : []),
            Sentry.consoleLoggingIntegration({
                levels: ['warn', 'error'],
            }),
        ],

        tracesSampleRate: 0.1,
        tracePropagationTargets: ['localhost', /^https:\/\/([^.]+\.)*comtev\.com\//],

        replaysSessionSampleRate: 0.05,
        replaysOnErrorSampleRate: 1.0,

        enableLogs: true,
        beforeSendLog: (log) => {
            if (log.level === 'trace' || log.level === 'debug') {
                return null;
            }
            return log;
        },
    });

    Sentry.getGlobalScope().setAttributes({
        service: 'comtev-frontend',
    });

    // React to a later consent decision: opt-in adds/starts replay, opt-out
    // stops it. (On a fresh visit Sentry inits before the banner is answered,
    // so the first opt-in is what actually turns replay on.)
    subscribeConsent((consent) => {
        const replay = Sentry.getReplay();
        if (consent?.analytics) {
            if (replay) {
                replay.start();
            } else {
                Sentry.addIntegration(makeReplay());
            }
        } else {
            replay?.stop();
        }
    });
}
